import { Trash2 } from 'lucide-react';
import { useEffect } from 'react';
import {
  type FieldArrayPath,
  useFieldArray,
  useFormContext,
} from 'react-hook-form';
import { toast } from 'sonner';
import z from 'zod';

import { useUploadCandidatesMutation } from '@/modules/candidates/mutations/useUploadCandidatesMutation';
import { Form } from '@/modules/forms/components/Form';
import {
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from '@/modules/forms/components/Forms';
import type { JobApi } from '@/modules/jobs/types';
import { Button } from '@/modules/ui/components/button/Button';
import { Input } from '@/modules/ui/components/input/Input';
import { extractErrorMessage } from '@/utils/extractErrorMessage';

interface LinkedInUploadFormProps {
  job: JobApi;
  onSuccess: () => void;
  onCancel?: () => void;
}

const formSchema = z.object({
  linkedInUrls: z
    .array(z.string())
    .min(1, 'At least one LinkedIn profile URL is required'),
});

type FormValues = z.input<typeof formSchema>;

export const LinkedInUploadForm = ({
  job,
  onSuccess,
  onCancel,
}: LinkedInUploadFormProps) => {
  const manualUploadMutation = useUploadCandidatesMutation();

  return (
    <Form
      schema={formSchema}
      onSubmit={(values) => {
        const linkedInUrls = values.linkedInUrls.filter(
          (url: string) => url && url.trim() !== '',
        );

        if (linkedInUrls.length === 0) {
          toast.error('Please enter at least one LinkedIn profile URL');
          return;
        }

        manualUploadMutation.mutate(
          {
            jobId: job.id,
            body: {
              linkedInProfileUrls: linkedInUrls,
              fileIds: [],
            },
          },
          {
            onSuccess: () => {
              toast.success(
                `Candidates have been uploaded. Uploaded ${linkedInUrls.length} LinkedIn ${linkedInUrls.length === 1 ? 'URL' : 'URLs'}`,
              );
              onSuccess();
            },
            onError: (error) => {
              toast.error(
                `Candidates have not been uploaded: ${extractErrorMessage(error)}`,
              );
            },
          },
        );
      }}
      defaultValues={{ linkedInUrls: [''] }}
    >
      <div className="space-y-6">
        <div className="max-h-[400px] overflow-y-auto">
          <LinkedInUrlFields />
        </div>
        <div className="flex justify-end gap-2">
          {onCancel && (
            <Button type="button" variant="outline" onClick={onCancel}>
              Cancel
            </Button>
          )}
          <Button type="submit" loading={manualUploadMutation.isPending}>
            Upload
          </Button>
        </div>
      </div>
    </Form>
  );
};

function LinkedInUrlFields() {
  const { control } = useFormContext<FormValues>();
  const { fields, append, remove } = useFieldArray({
    control,
    name: 'linkedInUrls' as FieldArrayPath<FormValues>,
  });

  // Ensure at least one field is always visible
  useEffect(() => {
    if (fields.length === 0) {
      append('');
    }
  }, [fields.length, append]);

  return (
    <div className="space-y-4">
      {fields.map((field, index) => (
        <FormField
          key={field.id}
          control={control}
          name={`linkedInUrls.${index}`}
          render={({ field: formField }) => (
            <FormItem>
              <FormControl>
                <div className="relative flex items-center gap-2">
                  <Input
                    placeholder="Enter LinkedIn profile URL"
                    {...formField}
                    onChange={(e) => {
                      formField.onChange(e);
                      const isLastField = index === fields.length - 1;
                      const isNotEmpty = e.target.value.trim() !== '';

                      if (isLastField && isNotEmpty) {
                        append('');
                      }
                    }}
                  />

                  {fields.length > 1 && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() => remove(index)}
                      className="h-9 w-9 shrink-0"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      ))}
    </div>
  );
}
